import { GraduationCap } from 'lucide-react';
import type { FleetInsightsDoc } from '../../services/fleetClientService';

export type GovernStance = 'required' | 'prohibited';

interface LearnedItem {
  text: string;
  detail?: string;
  // The learned insight, made governable: one click turns it into policy.
  govern?: { slug: string; name: string; stance: GovernStance };
}

// VCS plumbing and language/project-type detections are baseline — every
// repo trivially has them, so they are not meaningful "strengths".
const BASELINE_SLUGS = new Set([
  'git',
  'github',
  'gitlab',
  'bitbucket',
  'gitignore_config',
  'library_package',
  'cli_tool',
]);
const BASELINE_CATEGORIES = new Set([
  'source_control',
  'version_control',
  'language',
  'project_type',
]);

export function deriveStrengths(insights: FleetInsightsDoc): LearnedItem[] {
  const out: LearnedItem[] = [];
  const fleetSize = insights.fleet_size || 1;
  // Genuinely good, non-baseline practices the fleet actually adopts.
  Object.entries(insights.adoption)
    .filter(
      ([slug, a]) =>
        (a.kind === 'tool' || a.kind === 'practice') &&
        a.pct >= 0.5 &&
        !BASELINE_SLUGS.has(slug) &&
        !BASELINE_CATEGORIES.has(a.category),
    )
    .sort(([, a], [, b]) => b.pct - a.pct)
    .slice(0, 3)
    .forEach(([slug, a]) =>
      out.push({
        text: `${a.name} is used by ${Math.round(a.pct * 100)}% of the fleet`,
        detail: `${a.count}/${fleetSize} repos`,
        govern: { slug, name: a.name, stance: 'required' },
      }),
    );
  const best = insights.best_of_fleet?.top_repos?.slice(0, 2) ?? [];
  if (best.length) {
    out.push({
      text: `Your healthiest repos (${best.join(', ')}) set the internal bar`,
      detail: 'use them as the reference SDLC',
    });
  }
  insights.cooccurrence.slice(0, 1).forEach((r) =>
    out.push({
      text: `Consistent stack: ${r.if} repos also run ${r.then}`,
      detail: `${Math.round(r.confidence * 100)}% (support ${r.support})`,
    }),
  );
  return out.slice(0, 4);
}

export function deriveGaps(insights: FleetInsightsDoc): LearnedItem[] {
  const out: LearnedItem[] = [];
  // Open-source standard the fleet lacks — make it required.
  insights.exemplar_deltas.slice(0, 3).forEach((d) =>
    out.push({
      text: `${d.name} is standard in exemplar open source but rare here`,
      detail: `exemplars ${d.exemplar_adoption} vs fleet ${d.fleet_adoption}`,
      govern: { slug: d.slug, name: d.name, stance: 'required' },
    }),
  );
  // Legacy tools still in use — prohibit them fleet-wide.
  (insights.modernizations ?? [])
    .filter((m) => m.candidate_repos.length > 0)
    .slice(0, 2)
    .forEach((m) =>
      out.push({
        text: `${m.candidate_repos.length} repos still on ${m.legacy_name} (${m.label})`,
        detail: m.llm_free ? 'pushable now' : 'LLM-assisted migration',
        govern: { slug: m.from, name: m.legacy_name, stance: 'prohibited' },
      }),
    );
  const topIssue = insights.systemic_issues.find(
    (i) => i.severity === 'high' || i.severity === 'critical',
  );
  if (topIssue) {
    out.push({
      text: topIssue.title,
      detail: `${topIssue.affected_count} repos · ${topIssue.severity}`,
    });
  }
  return out.slice(0, 4);
}

export function LearnedList({
  items,
  onGovern,
  governed,
}: {
  items: LearnedItem[];
  onGovern?: (slug: string, name: string, stance: GovernStance) => void;
  governed?: Set<string>;
}) {
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="text-gray-800 dark:text-gray-200">
          <div className="flex items-start justify-between gap-2">
            <span className="flex-1">{it.text}</span>
            {it.govern &&
              onGovern &&
              (governed?.has(it.govern.slug) ? (
                <span className="shrink-0 text-[11px] text-gray-400">governed</span>
              ) : (
                <button
                  onClick={() => onGovern(it.govern!.slug, it.govern!.name, it.govern!.stance)}
                  className="shrink-0 text-[11px] px-2 py-0.5 rounded-full border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 transition-colors hover:bg-teal-100 dark:hover:bg-teal-900/40"
                  title={`Make ${it.govern.name} ${it.govern.stance} across the fleet`}
                >
                  {it.govern.stance === 'required' ? 'Require' : 'Prohibit'}
                </button>
              ))}
          </div>
          {it.detail && (
            <span className="block text-xs text-gray-500 dark:text-gray-400">{it.detail}</span>
          )}
        </li>
      ))}
      {items.length === 0 && <li className="text-gray-500 text-xs">—</li>}
    </ul>
  );
}

export function WhatAutarLearned({
  insights,
  reportHref,
  onGovern,
  governed,
}: {
  insights: FleetInsightsDoc;
  reportHref?: string;
  onGovern?: (slug: string, name: string, stance: GovernStance) => void;
  governed?: Set<string>;
}) {
  const strengths = deriveStrengths(insights);
  const gaps = deriveGaps(insights);
  return (
    <section className="p-5 rounded-2xl border border-teal-200/70 dark:border-teal-900/60 bg-teal-50/60 dark:bg-teal-950/20 shadow-sm">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30">
            <GraduationCap className="w-4 h-4 text-teal-600 dark:text-teal-400" />
          </span>
          <h2 className="font-semibold text-gray-900 dark:text-white">What Autar learned</h2>
        </div>
        {reportHref && (
          <a href={reportHref} className="text-xs text-teal-700 dark:text-teal-400 hover:underline">
            Full report →
          </a>
        )}
      </div>
      {onGovern && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Turn any insight into a fleet policy — Require a strength, Prohibit a gap.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <h3 className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide mb-2">
            Strengths
          </h3>
          <LearnedList items={strengths} onGovern={onGovern} governed={governed} />
        </div>
        <div>
          <h3 className="text-xs font-medium text-red-700 dark:text-red-400 uppercase tracking-wide mb-2">
            Gaps vs open source
          </h3>
          <LearnedList items={gaps} onGovern={onGovern} governed={governed} />
        </div>
      </div>
    </section>
  );
}
